import { useEffect, useState, memo } from 'react';
import ProductImagePlaceholder from './ProductImagePlaceholder';

const READ_MORE_THRESHOLD = 40; // show "Read more" when the description is longer than this

function ProductCard({ product, isSelected, cartQuantity, selectedCustomization, onAddToCart, onRemove, onQuantityChange }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [localQty, setLocalQty] = useState(1);
  const [needsCustomization, setNeedsCustomization] = useState(false);

  const isOutOfStock = product.outOfStock === true;
  const hasImage = product.image && !imageError;

  const getMaxStock = () => {
    const n = parseFloat(product.stock);
    if (isNaN(n) || n <= 0) return 999; // unknown/N-A stock — don't cap
    return Math.floor(n);
  };
  const maxStock = getMaxStock();
  const customizationLocked = isSelected;
  const showCustomizationControls = !customizationLocked || needsCustomization;
  const customizationHelpText = customizationLocked
    ? (needsCustomization
      ? 'Customization is already saved for this cart item. Remove it from the cart to change the upload.'
      : 'To add customization, remove this item from the cart and add it again with customization selected.')
    : 'If selected, you will be asked to upload the design file after clicking Add to Cart.';

  // When in the cart, the stepper reflects/edits the cart quantity; otherwise a local pending qty.
  const displayQty = isSelected ? cartQuantity : localQty;

  useEffect(() => {
    if (isSelected) {
      setNeedsCustomization(selectedCustomization === 'yes');
    }
  }, [isSelected, selectedCustomization]);

  const step = (delta) => {
    const next = Math.min(Math.max(1, displayQty + delta), maxStock);
    if (next === displayQty) return;
    if (isSelected) onQuantityChange(product.id, next);
    else setLocalQty(next);
  };

  const handlePrimary = () => {
    if (isSelected) onRemove(product.id);
    else onAddToCart(product, localQty, {
      customization: needsCustomization ? 'yes' : 'no'
    });
  };

  const handleImageClick = () => {
    if (hasImage) setShowLightbox(true);
  };

  return (
    <>
      <div className={`product-card ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}>
        {/* Top box — image (60%) */}
        <div className="card-image-box" onClick={handleImageClick}>
          {hasImage ? (
            <>
              {!imageLoaded && <div className="image-skeleton" />}
              <img
                src={product.image}
                alt={product.description}
                className={`product-image ${imageLoaded ? 'loaded' : ''}`}
                onError={() => setImageError(true)}
                onLoad={() => setImageLoaded(true)}
                loading="lazy"
                decoding="async"
              />
            </>
          ) : (
            <ProductImagePlaceholder
              label={imageError ? 'Image not found' : 'No image'}
            />
          )}

          {isOutOfStock && <div className="out-of-stock-badge">Out of Stock</div>}
          {isSelected && !isOutOfStock && <div className="in-cart-badge">In cart</div>}
        </div>

        {/* Bottom box — info + actions (40%) */}
        <div className="card-info-box">
          <div className="card-name-row">
            <div className={`card-desc ${expanded ? 'expanded' : ''}`}>
              {product.description}
            </div>
            {product.price && <span className="card-price">₹{product.price}</span>}
          </div>
          {product.description && product.description.length > READ_MORE_THRESHOLD && (
            <button className="read-more-btn" onClick={() => setExpanded(e => !e)}>
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
          <div className="card-sku">{product.sku}</div>

          {!isOutOfStock && (
            <div className="card-customization-block">
              {showCustomizationControls && (
                <div className="card-customization-row">
                  <label className={`card-customization-toggle ${needsCustomization ? 'checked' : ''} ${customizationLocked ? 'locked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={needsCustomization}
                      disabled={customizationLocked}
                      onChange={(e) => setNeedsCustomization(e.target.checked)}
                    />
                    <span className="card-customization-box" aria-hidden="true">
                      {needsCustomization ? '✓' : ''}
                    </span>
                    <span className="card-customization-label">Need Customization</span>
                  </label>
                  <span className="card-customization-info" tabIndex={0} aria-label="Customization help">
                    <span className="card-info-icon">i</span>
                    <span className="card-info-tooltip">{customizationHelpText}</span>
                  </span>
                </div>
              )}
              {customizationLocked && !needsCustomization && (
                <p className="card-customization-note warning standalone">
                  To add customization, remove this item from the cart and add it again.
                </p>
              )}
              {customizationLocked && needsCustomization && (
                <p className="card-customization-note info">
                  Customization is locked after adding to cart. Remove the item to change the upload.
                </p>
              )}
            </div>
          )}

          <div className="card-actions">
            {isOutOfStock ? (
              <span className="out-of-stock-text">Out of stock</span>
            ) : (
              <>
                <div className="qty-stepper">
                  <button onClick={() => step(-1)} disabled={displayQty <= 1} aria-label="Decrease quantity">&minus;</button>
                  <span className="qty-value">{displayQty}</span>
                  <button onClick={() => step(1)} disabled={displayQty >= maxStock} aria-label="Increase quantity">+</button>
                </div>
                <button
                  className={`add-cart-btn ${isSelected ? 'in-cart' : ''}`}
                  onClick={handlePrimary}
                  title={isSelected ? 'Remove from cart' : 'Add to cart'}
                >
                  {isSelected ? 'Remove' : 'Add to Cart'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showLightbox && hasImage && (
        <div className="lightbox-overlay" onClick={() => setShowLightbox(false)}>
          <button className="lightbox-close" onClick={() => setShowLightbox(false)}>&times;</button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={product.image} alt={product.description} className="lightbox-image" decoding="async" />
            <div className="lightbox-caption">
              <span className="lightbox-sku">{product.sku}</span>
              <span className="lightbox-desc">{product.description}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Memoized: cards only re-render when their own props change (product, isSelected,
// cartQuantity). App's callbacks are useCallback-stable, so the shallow compare holds.
export default memo(ProductCard);
